import { useState } from 'react';
import { Mic, MicOff, NotebookPen } from 'lucide-react';
import { useMeetingNotes, SPEECH_LANGUAGES } from '../lib/meetingNotes';
import { companionPalette } from '../lib/avatarArt';
import { useMe } from '../lib/auth';
import { cx } from './ui';

/**
 * Centres its child at the top of the screen. A wrapper rather than a
 * translate on the card itself, because the slide-in animation owns the
 * card's transform and would undo the centring.
 */
function TopCentre({ children }: { children: React.ReactNode }) {
  return <div className="pointer-events-none fixed inset-x-0 top-20 z-40 flex justify-center px-4">{children}</div>;
}

/**
 * What everyone in a room sees when someone's Mochi is taking notes there:
 * who sent it, and a clear yes/no before anything they say is noted. Once
 * they are included, a small pill says it is listening and lets them stop.
 * Shown on every page, because you are still in the room when you look at
 * something else.
 */
export function MeetingNotesLayer() {
  const { notes, consent, speechSupported, listening, language, setLanguage } = useMeetingNotes();
  const me = useMe();
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const here = notes.find((note) => note.status === 'ACTIVE' && note.inRoom) ?? null;
  if (!here) return null;

  const owner = here.sender.id === me.user.id ? `Your ${here.companion.name}` : `${here.sender.name.split(' ')[0]}’s ${here.companion.name}`;
  const palette = companionPalette(here.companion.hue);

  const answer = async (included: boolean) => {
    setPending(included ? 'yes' : 'no');
    setError(null);
    try {
      await consent(here.id, included);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That did not save');
    } finally {
      setPending(null);
    }
  };

  if (here.myConsent === null) {
    return (
      <TopCentre>
      <section
        role="alertdialog"
        aria-labelledby="meeting-notes-title"
        className="glass-strong pointer-events-auto w-full max-w-[30rem] animate-slide-up rounded-3xl p-4"
      >
        <div className="flex items-start gap-3">
          <span
            className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-full text-white"
            style={{ background: `radial-gradient(circle at 35% 30%, ${palette.light}, ${palette.base} 55%, ${palette.dark})` }}
            aria-hidden
          >
            <NotebookPen className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id="meeting-notes-title" className="font-semibold text-slate-900">
              {owner} is taking notes in {here.roomName}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Only people who agree are included. If you say yes, your browser turns what you say into text while you’re in this room
              with your mic on, and you’ll get the minutes. Mochi never receives audio. You can stop at any time.
            </p>
            {here.focus ? <p className="mt-1 text-xs text-slate-500">Listening for: {here.focus}</p> : null}
            {!speechSupported ? (
              <p className="mt-2 text-xs text-amber-800">This browser can’t transcribe speech (try Chrome, Edge or Safari), so you can only be left out here.</p>
            ) : (
              <p className="mt-2 text-[11px] text-slate-400">Your browser’s speech recognition may send audio to its provider (Google or Apple) to turn it into text.</p>
            )}
            {error ? (
              <p className="mt-2 text-sm text-rose-700" role="alert">
                {error}
              </p>
            ) : null}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button type="button" className="btn-primary px-4 py-1.5 text-sm" onClick={() => void answer(true)} disabled={pending !== null || !speechSupported}>
                Include me
              </button>
              <button type="button" className="btn-secondary px-4 py-1.5 text-sm" onClick={() => void answer(false)} disabled={pending !== null}>
                Leave me out
              </button>
              {speechSupported ? (
                <label className="ml-auto flex items-center gap-1.5 text-xs text-slate-500">
                  I’ll speak
                  <select className="input w-auto py-1 text-xs" value={language} onChange={(event) => setLanguage(event.target.value)}>
                    {SPEECH_LANGUAGES.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </div>
        </div>
      </section>
      </TopCentre>
    );
  }

  if (!here.myConsent) return null;

  return (
    <TopCentre>
    <div className="glass-strong pointer-events-auto flex animate-slide-up items-center gap-2 rounded-full px-3 py-1.5 text-sm" role="status">
      {listening === 'on' ? (
        <span className="relative flex h-2.5 w-2.5" aria-hidden>
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
        </span>
      ) : listening === 'paused-muted' ? (
        <MicOff className="h-4 w-4 text-slate-500" aria-hidden />
      ) : (
        <Mic className="h-4 w-4 text-slate-400" aria-hidden />
      )}
      <span className={cx('text-slate-700', listening === 'error' && 'text-rose-700')}>
        {listening === 'on'
          ? `${owner} is noting what you say`
          : listening === 'paused-muted'
            ? 'Muted — nothing is being noted'
            : listening === 'error'
              ? 'Microphone blocked — nothing is being noted'
              : `Included in ${owner}’s notes`}
      </span>
      <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={() => void answer(false)} disabled={pending !== null}>
        Stop including me
      </button>
    </div>
    </TopCentre>
  );
}
